import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Accordion, AccordionDetails, AccordionSummary, Button, Chip, IconButton, Stack, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LaunchIcon from '@mui/icons-material/Launch'

import DoctorIcon from '@/components/ui/icons/Doctor'
import CalendarIcon from '@/components/ui/icons/Calendar'
import PdsIcon from '@/components/ui/icons/Pds'
import { getStatusColor } from '@/utils/consultations'

import { usePatientConsultations } from '../model/usePatientConsultations'
import { usePatientConsultationResponsive } from '../model/usePatientConsultationResponsive'
import { ConsultationDetails } from './ConsultationDetails'

type PatientConsultationProps = {
  consultation: ReturnType<typeof usePatientConsultations>['patientHistoricalConsultations'][number]
}

export function PatientConsultation({ consultation }: PatientConsultationProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const { containerRef, isExtraSmall, shouldStackMain, shouldWrapDetails } = usePatientConsultationResponsive()

  return (
    <Stack ref={containerRef}>
      <Accordion expanded={expanded} onChange={(_event, isExpanded) => setExpanded(isExpanded)} slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            direction={shouldStackMain ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={shouldStackMain ? 'flex-start' : 'center'}
            spacing={shouldStackMain ? 1.5 : 2}
            width="100%"
            pr={1}
          >
            {/* Names section */}
            <Stack direction="row" alignItems="center" spacing={isExtraSmall ? 1 : 2} flexWrap="wrap" sx={{ gap: 1 }}>
              {consultation.pds?.fullName && (
                <Stack direction="row" alignItems="center" spacing="10px">
                  <PdsIcon isFemale={consultation.pds?.gender === 'F'} />
                  <Typography variant={isExtraSmall ? 'body2' : 'body1'}>{consultation.pds.fullName}</Typography>
                </Stack>
              )}
              {consultation.doctor?.fullName && (
                <Stack direction="row" alignItems="center" spacing="10px">
                  <DoctorIcon isFemale={consultation.doctor?.gender === 'F'} />
                  <Typography variant={isExtraSmall ? 'body2' : 'body1'}>{consultation.doctor.fullName}</Typography>
                </Stack>
              )}
            </Stack>

            {/* Details section */}
            <Stack
              direction={shouldWrapDetails ? 'column' : 'row'}
              justifyContent="start"
              alignItems={shouldWrapDetails ? 'flex-start' : 'center'}
              spacing={shouldWrapDetails ? 1 : 2}
              sx={{ width: shouldStackMain ? '100%' : 'auto' }}
            >
              {/* Date, type, and status chips */}
              <Stack direction="row" alignItems="center" spacing={isExtraSmall ? 0.5 : 1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                <CalendarIcon fontSize="small" />
                <Chip
                  variant="filled"
                  color="secondary"
                  size={isExtraSmall ? 'small' : 'medium'}
                  label={
                    <span>
                      {dayjs(consultation.startDate).isSame(dayjs(), 'day') ? t(`moment:calendar:today`) : dayjs(consultation.startDate).format('DD/MM/YYYY')} -{' '}
                      <b>{dayjs(consultation.startDate).format('HH[h]mm')}</b>
                    </span>
                  }
                />
                {consultation.type && (
                  <Chip
                    variant="outlined"
                    color="default"
                    label={t(`page.consultations.consultationsSearchForm.field.searchByType.option.${consultation.type}.label`)}
                    size="small"
                  />
                )}
                <Chip
                  color={getStatusColor(consultation.status)}
                  label={t(`common:model.consultation.status.${consultation.status}.label`)}
                  size="small"
                  variant="filled"
                />
              </Stack>

              {/* Actions section */}
              <Stack direction="row" alignItems="center" spacing={isExtraSmall ? 0.5 : 1}>
                <Button variant="outlined" size={isExtraSmall ? 'small' : 'medium'}>
                  {consultation.fileCount} doc{consultation.fileCount > 1 ? 's' : <span>&nbsp;&nbsp;</span>}
                </Button>
                <IconButton size="small" onClick={() => window.open(`/consultations/${consultation.id}`, '_blank')}>
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <ConsultationDetails consultation={consultation} />
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}
